import React, { useEffect, useMemo, useRef, useState } from "react";
import "./User.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { validateLobbyPin } from "../../functions/validateLobbyPin";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { isAnchorModeFromSettings } from "../../lib/gameMode";
import { swalTabooTheme } from "../../lib/swalTabooTheme";
import logoImg from "../images/desktop/logo.png";

const DEFAULT_ROUNDS = 3;
const DEFAULT_TIME = 1;

export default function User() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth?.user);
  const adminToken = useSelector(selectAdminToken);

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = user?.sessionId ?? user?.sessionid ?? "";
    const oid = user?.organizationId ?? user?.orgId ?? user?.organizationid ?? "";
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, user]);

  const [savingRole, setSavingRole] = useState(null);
  const [saveError, setSaveError] = useState("");
  const stageIdRef = useRef(null);
  const reportIdRef = useRef(null);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [dispatch]);

  useEffect(() => {
    document.documentElement.classList.add("user-role-static-bg");
    document.body.classList.add("user-role-static-bg");
    return () => {
      document.documentElement.classList.remove("user-role-static-bg");
      document.body.classList.remove("user-role-static-bg");
    };
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    try {
      const raw = sessionStorage.getItem("gameSettings");
      if (!raw) return;
      const cached = JSON.parse(raw);
      if (cached?.stageId) stageIdRef.current = String(cached.stageId);
      if (cached?.reportId) reportIdRef.current = String(cached.reportId);
    } catch {
      // ignore
    }
  }, [user, navigate]);

  const persistSettings = (mode, extra = {}) => {
    const settings = {
      mode,
      rounds: DEFAULT_ROUNDS,
      questions: DEFAULT_ROUNDS,
      time: DEFAULT_TIME,
      isAnchorMode: isAnchorModeFromSettings({ mode, ...extra }),
      reportId: reportIdRef.current,
      stageId: stageIdRef.current,
      ...extra,
    };
    sessionStorage.setItem("gameSettings", JSON.stringify(settings));
    return settings;
  };

  const saveRoleToDb = async (current_mode) => {
    const uid = user?.userId ?? user?.userid ?? user?.id;
    if (!tokenForApi || !uid) throw new Error("Missing session or user");
    const upd = await updateGameSettings({
      token: tokenForApi,
      userId: uid,
      stageId: stageIdRef.current,
      themeName: "guess_me",
      user_type: user?.user_type ?? user?.role ?? null,
      current_mode,
      questions_no: DEFAULT_ROUNDS,
      time_duration: DEFAULT_TIME,
      show_question: true,
      email: user?.email ?? null,
      fullName: user?.fullName ?? user?.name ?? user?.username ?? null,
      employeeId: user?.employeeId ?? user?.employeeid ?? null,
      reportid: user?.reportId ?? user?.reportid ?? null,
    });
    if (upd?.stageId != null) stageIdRef.current = String(upd.stageId);
    return upd;
  };

  const wrongLobbyPinPopup = async () => {
    await Swal.fire(
      swalTabooTheme({
      icon: "error",
      title: "Wrong Lobby PIN",
      text: "Please check the PIN and try again.",
      confirmButtonText: "OK",
      })
    );
  };

  const joinLobbyWithPin = async (pin) => {
    const resp = await validateLobbyPin({ token: tokenForApi, lobby_pin: pin });
    if (!resp?.match) {
      await wrongLobbyPinPopup();
      return;
    }
    const uid = user?.userId ?? user?.userid ?? user?.id;
    if (!uid) throw new Error("Missing userId");

    let existingStageId = stageIdRef.current;
    if (!existingStageId) {
      try {
        const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
        existingStageId = cached?.stageId ?? null;
      } catch {
        existingStageId = null;
      }
    }

    const joinTheme = resp.themeName ?? "guess_me";
    const upd = await updateGameSettings({
      token: tokenForApi,
      userId: uid,
      ...(existingStageId ? { stageId: existingStageId } : {}),
      themeName: joinTheme,
      current_mode: "Player",
      lobby_name: resp.lobby_name ?? null,
      lobby_pin: resp.lobby_pin ?? pin,
      show_question: true,
      ...(resp.time_duration != null ? { time_duration: resp.time_duration } : {}),
    });
    if (upd?.stageId) stageIdRef.current = String(upd.stageId);

    const settings = persistSettings("Player", {
      lobby_name: resp.lobby_name ?? null,
      lobby_pin: resp.lobby_pin ?? pin,
      themeName: joinTheme,
      hostUserId: resp.hostUserId ?? null,
      lobby_link: resp.lobby_link ?? null,
    });

    navigate("/joinedLobby", {
      replace: true,
      state: {
        lobby_name: resp.lobby_name,
        stageId: stageIdRef.current,
        themeName: joinTheme,
        lobby_pin: resp.lobby_pin ?? pin,
        backTo: "/user",
        settings,
      },
    });
  };

  const handleHost = async () => {
    if (!user || savingRole) return;
    setSaveError("");
    setSavingRole("host");
    try {
      const upd = await saveRoleToDb("Host");
      const settings = persistSettings("Host", {
        isAnchorMode: true,
        lobby_name: upd?.lobby_name ?? null,
        lobby_pin: upd?.lobby_pin ?? null,
      });
      navigate("/manager-dashboard", {
        replace: true,
        state: { reportId: reportIdRef.current, settings, isAnchorMode: true },
      });
    } catch (e) {
      setSaveError(e?.message || "Failed to save role");
    } finally {
      setSavingRole(null);
    }
  };

  const handlePlayer = async () => {
    if (!user || savingRole) return;
    setSaveError("");
    setSavingRole("player");
    try {
      await saveRoleToDb("Player");
      persistSettings("Player", { isAnchorMode: false });
    } catch (e) {
      setSaveError(e?.message || "Failed to save role");
      setSavingRole(null);
      return;
    }
    setSavingRole(null);

    const { value: pin, isDismissed } = await Swal.fire(
      swalTabooTheme({
      icon: "info",
      title: "Join Lobby",
      text: "Enter the 5-digit Lobby PIN from your host.",
      input: "text",
      inputPlaceholder: "Lobby PIN",
      inputAttributes: { maxlength: 5, inputmode: "numeric", pattern: "[0-9]*" },
      showCancelButton: true,
      confirmButtonText: "Join",
      preConfirm: (val) => {
        const p = String(val || "").trim();
        if (!/^\d{5}$/.test(p)) {
          Swal.showValidationMessage("PIN must be 5 digits");
          return false;
        }
        return p;
      },
      })
    );

    if (isDismissed || !pin) return;

    setSavingRole("player");
    try {
      await joinLobbyWithPin(String(pin).trim());
    } catch (e) {
      await Swal.fire(
        swalTabooTheme({
          icon: "error",
          title: "Error",
          text: e?.message || "Failed to join lobby",
        })
      );
    } finally {
      setSavingRole(null);
    }
  };

  return (
    <main className="user-role-page" aria-label="Choose your role">
      <div className="user-role-confetti" aria-hidden="true" />
      <img src={logoImg} className="user-role-logo" alt="Taboo Live" />
      <div className="user-role-actions">
        <button
          type="button"
          className="user-role-btn user-role-btn--host"
          onClick={() => void handleHost()}
          disabled={Boolean(savingRole)}
          aria-label="Host"
        >
          <span>{savingRole === "host" ? "…" : "Host"}</span>
        </button>
        <button
          type="button"
          className="user-role-btn user-role-btn--player"
          onClick={() => void handlePlayer()}
          disabled={Boolean(savingRole)}
          aria-label="Player"
        >
          <span>{savingRole === "player" ? "…" : "Player"}</span>
        </button>
      </div>
      {saveError ? <p className="user-role-error">{saveError}</p> : null}
    </main>
  );
}
